import { useNavigate, useParams } from 'react-router-dom'

import { Form } from 'factorial-form'
import { ArchiveWorkScheduleModalPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateToString, dateUIToBackend } from 'modules/core/lib/dates'

import PlanningVersionsApi from 'modules/time_planning/store/collections/planningVersions'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'
import ArchiveConfirmationModal from 'modules/work_schedules/components/ArchiveConfirmationModal'
import schedules from 'modules/work_schedules/store/collections/schedules'

const ArchiveWorkScheduleModalPage = () => {
  // FIXME: Remove this once we have it working on GraphQL
  const { loading } = useAsyncData(() => Promise.all([schedules.fetch()]), [])

  const { navigation } = useNavigation()

  const { id } = useParams()
  const { data, loading: loadingGraphql } = useQuery(ArchiveWorkScheduleModalPageDocument, {
    variables: {
      scheduleId: Number(id),
    },
  })
  // get current WS id you want to archive
  const workSchedule = schedules.get(Number(id))

  // get the employees ids from current WS
  const workScheduleEmployeesIds = workSchedule?.employeeIds.slice()
  const noOfEmployees = workScheduleEmployeesIds ? workScheduleEmployeesIds.length : 0

  const canReassignAll = workSchedule
    ? data?.workSchedule.schedule?.canReassignEmployeesPolicy.can ?? false
    : false
  const canAssignEmployees = workSchedule
    ? data?.workSchedule.schedule?.canAssignEmployeesPolicy.can ?? false
    : false

  const canArchive = !noOfEmployees || canReassignAll

  const form = useLazyRef(
    () =>
      new Form(
        {
          effective_at: dateToString(moment(), 'backend'),
        },
        {
          work_schedule_id: 'string',
          effective_at: 'date',
        }
      )
  ).current

  const archiveWorkSchedule = async (closePortal: ClosePortalFn) => {
    if (!canArchive || !workSchedule || !form.get('work_schedule_id')) {
      return
    }

    // In the case that there are no employees assigned to it
    if (!noOfEmployees) {
      const result = await asyncAction(() => workSchedule.archive())

      if (isSuccessful(result)) {
        closePortal()

        return
      }
    }

    // get the WS you want to reassign the employees to
    const selectedWorkScheduleId = form.get('work_schedule_id').value
    const effectiveAt = dateUIToBackend(form.get('effective_at').value)

    const result = await asyncAction(() =>
      // reassign the archived WS employees to the selected WS by creating new PV
      PlanningVersionsApi.rpc('bulk_create', {
        employee_ids: workScheduleEmployeesIds,
        effective_at: effectiveAt,
        planning_tool: PLANNING_TOOL.workSchedule,
        schedule_id: +selectedWorkScheduleId,
      })
    )

    if (isSuccessful(result)) {
      // archive WS
      await workSchedule?.rpc('toggle_archive')

      await schedules.fetch()
      closePortal()
    }
  }

  const getOptions = () => {
    return schedules.models
      .filter(
        (schedule) =>
          schedule.attributes.id !== Number(id) && schedule.attributes.archived_at === null
      )
      .map((schedule) => ({
        label: schedule.attributes.name,
        value: schedule.attributes.id,
      }))
  }

  const options = !schedules ? [] : getOptions()
  const navigate = useNavigate()

  const onClose = () => {
    navigate(navigation.settings.workSchedule.fullPath())
  }

  if (loading || loadingGraphql) {
    return <Loading />
  }

  return (
    <ArchiveConfirmationModal
      noOfEmployees={noOfEmployees}
      onClose={onClose}
      onConfirm={archiveWorkSchedule}
      options={options}
      form={form}
      canArchive={canArchive}
      canReassignAll={canReassignAll}
      canAssignEmployees={canAssignEmployees}
      loading={loading}
    />
  )
}

export default ArchiveWorkScheduleModalPage
